import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { EBundlesViewModel } from '@appcharge/shared-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import useFeatureFlags from 'api/useFeatureFlags';
import useOffers from 'api/useOffers';
import usePermissions from 'api/usePermissions';
import usePricing from 'api/usePricing';
import { useProduct } from 'api/useProduct';
import useSections from 'api/useSections';
import useTabs from 'api/useTabs';
import useTheme from 'api/useTheme';
import {
  EBadgeType,
  ETaskId,
  Offer,
  ProductsSequenceProduct,
  Section
} from 'common/contracts';
import {
  EAssetType,
  EFeatureFlag,
  EProductPriority,
  ESaleDiscountDisplayType,
  ESectionOfferType,
  OfferType
} from 'constants/enums';
import Decimal from 'decimal.js';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';
import { convertProgressBarPointsToApiFormat } from 'utils/barPointsUtils';
import { transformQuantityValue } from 'utils/formattingUtil';
import { isPriceLocalized as isPriceLocalizedUtil } from 'utils/isPriceLocalized';
import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';
import {
  badgeFieldsSchema,
  BadgesSection,
  generalFieldsSchema,
  GeneralSection,
  OfferProgressBarSection,
  productsFieldsSchema,
  ProductsSection,
  saleDiscountFieldsSchema,
  SaleDiscountSection,
  SegmentsSection,
  TagsSection,
  segmentsFieldsSchema,
  standardPriceRefinement,
  tagsFieldsSchema
} from 'views/Offers/components/sections';
import { useBadgeForm } from 'views/Offers/hooks/useBadgeForm';
import { useOfferDesignOptions } from 'views/Offers/hooks/useOfferDesignOptions';
import { useOfferFormData } from 'views/Offers/hooks/useOfferFormData';
import { useOfferFormSubmit } from 'views/Offers/hooks/useOfferFormSubmit';
import { useProgressBarForm } from 'views/Offers/hooks/useProgressBarForm';
import { useProgressBarOffers } from 'views/Offers/hooks/useProgressBarOffers';
import {
  BUNDLE_MODEL_BADGE_MAP,
  DEFAULT_OFFER_DESIGN_EXTERNAL_ID,
  EMPTY_TAB
} from 'views/Offers/Offer.consts';
import { IProductProps } from 'views/Offers/Offer.types';
import {
  buildBaseOfferPayload,
  buildFormTitle,
  buildOfferBadges,
  buildProductsListForPreview,
  buildSectionTabPayload,
  checkUniqueness,
  createBadgeFilter,
  getInitialBadgeValues,
  getRelevantBadgeType,
  mapApiProductsToLocal
} from 'views/Offers/Offer.utils';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';
import { z } from 'zod';

import { Product } from 'components/ProductsForm/ProductsForm.types';
import { TabsItem } from 'components/Theme/StoreTheme/storeTheme.types';
import { Form } from 'components/ui';
import { Separator } from 'components/ui/separator';

import { BundlePreview } from './BundlePreview';
import { buildBundleFormInitialValues } from './BundlesForm.utils';

import 'style/forms.scss';

const BUNDLES_SCHEMA = standardPriceRefinement(
  generalFieldsSchema
    .merge(saleDiscountFieldsSchema)
    .merge(productsFieldsSchema)
    .merge(tagsFieldsSchema)
    .merge(segmentsFieldsSchema)
    .merge(badgeFieldsSchema)
    .extend({
      type: z.string().optional().default(''),
      quantity: z.string().optional().default(''),
      productsSequence: z.array(z.any()).optional(),
      backgroundImage: z.string().optional().default(''),
      active: z.boolean().default(true)
    })
);

type BundlesFormFields = z.infer<typeof BUNDLES_SCHEMA>;

interface IBundlesFormProps {
  edit?: boolean;
  dup?: boolean;
}

export const BundlesForm: React.FC<IBundlesFormProps> = ({
  edit = false,
  dup = false
}): React.ReactElement => {
  const { publisherId, offerId } = useParams();
  const navigate = useNavigate();
  const { canUserEdit } = usePermissions();
  const { featureFlags } = useFeatureFlags({ publisherId });
  const { addOffer, updateOffer, getOffer, getOffers, formatProductQuantity } =
    useOffers({ offerId });
  const { setTaskStatus, hasCategories, isWebStore } = useChecklistState();

  const { submitOffer } = useOfferFormSubmit({
    edit,
    dup,
    offerLabel: 'Offer',
    addOffer,
    updateOffer,
    onSuccess: (): void => {
      if (!edit && hasCategories && isWebStore) {
        setTaskStatus(ETaskId.ACTIVATE_FIRST_BUNDLE);
      }
    }
  });

  const {
    offerDesignOptions,
    defaultOfferDesignId,
    getOfferUI,
    setCurrentOfferUI
  } = useOfferDesignOptions({
    publisherId: publisherId || '',
    offerDesignType: OfferDesignTypes.Bundle,
    defaultExternalId: DEFAULT_OFFER_DESIGN_EXTERNAL_ID[OfferType.BUNDLE],
    offerId
  });

  const hasFeatureFlagTags = featureFlags?.[EFeatureFlag.DASHBOARD_TAGS];
  const { getGeneralTheme: generalTheme } = useTheme(publisherId);

  const [products, setProducts] = useState<IProductProps[]>([]);
  const [currentProduct, setCurrentProduct] =
    useState<ProductsSequenceProduct | null>(null);
  const [dirtyProducts, setDirtyProducts] = useState(false);

  const {
    progressBarFormValues,
    setProgressBarFormValues,
    isProgressBarFormValid,
    dirtyProgressBar,
    handleProgressBarFormChange
  } = useProgressBarForm();

  const { progressBarOffers } = useProgressBarOffers();

  const handleDataLoaded = useCallback(
    (result: Partial<Offer>): void => {
      setCurrentOfferUI(
        result.offerUi?._id || result.offerUi?.offerUiId || ''
      );
      setProducts(
        mapApiProductsToLocal(
          (result.productsSequence?.[0]?.products as any[]) || []
        )
      );
      setProgressBarFormValues({
        progressBarPoints:
          result.productsSequence?.[0]?.progressBarPoints?.[0]?.points || null,
        progressBarId:
          result.productsSequence?.[0]?.progressBarPoints?.[0]?.barId || null
      });
    },
    [setCurrentOfferUI, setProgressBarFormValues]
  );

  const { data, isLoading, chosenSegment, setChosenSegment } =
    useOfferFormData({
      offerId,
      publisherId,
      getOffer,
      onDataLoaded: handleDataLoaded
    });

  const { getSectionsByOfferType: getSections } = useSections(
    publisherId,
    ESectionOfferType.BUNDLE
  );
  const { getTabs } = useTabs(publisherId);

  const { data: productsData, isLoading: isLoadingProducts } =
    useProduct(publisherId).getProducts;
  const productsList: Product[] = productsData?.result;

  const { getPricings } = usePricing();
  const [isPriceLocalized, setIsPriceLocalized] = useState(true);
  const priceTypingDebounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const viewModel = generalTheme.data?.bundlesViewModel as EBundlesViewModel;
  const bundleModel = useMemo(
    () => generalTheme.data?.bundlesInternalViewModel,
    [generalTheme.data?.bundlesInternalViewModel]
  );

  const enableTabs: boolean = useMemo(
    () => featureFlags?.[EFeatureFlag.STORE_TABS],
    [featureFlags]
  );

  const relevantBadgeType: EBadgeType | 'both' = useMemo(
    () => getRelevantBadgeType(bundleModel, BUNDLE_MODEL_BADGE_MAP, viewModel),
    [viewModel, bundleModel]
  );

  const initialBadgeValues = useMemo(
    () =>
      getInitialBadgeValues(
        data?.badges,
        createBadgeFilter(relevantBadgeType, viewModel)
      ),
    [data?.badges, relevantBadgeType, viewModel]
  );

  const {
    badgeList,
    emblemBadges,
    ribbonBadges,
    setSelectedBadgeId,
    selectedPublisherBadgeId
  } = useBadgeForm({
    publisherId,
    relevantBadgeType,
    viewModel,
    initialSelectedBadgeId: initialBadgeValues.badgeId
  });

  const initialValues = useMemo(
    () => ({
      ...buildBundleFormInitialValues({
        data,
        edit,
        dup,
        defaultOfferDesignId,
        getOfferResult: getOffer?.data?.result
      }),
      badge: initialBadgeValues.badgeId,
      publisherBadgeId: initialBadgeValues.publisherBadgeId
    }),
    [data, edit, dup, defaultOfferDesignId, getOffer?.data?.result, initialBadgeValues]
  );

  const form = useForm<BundlesFormFields>({
    resolver: zodResolver(BUNDLES_SCHEMA),
    mode: 'onChange',
    defaultValues: initialValues
  });

  const {
    watch,
    handleSubmit,
    setValue,
    formState: { isDirty, isValid },
    reset
  } = form;

  const values = watch();

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  useEffect(() => {
    if (!getPricings.data && !getPricings.isFetching) {
      getPricings.refetch();
    }
  }, [getPricings.data, getPricings.isFetching]);

  const watchedBadge = useWatch({ control: form.control, name: 'badge' });
  useEffect(() => {
    setSelectedBadgeId(watchedBadge ?? null);
  }, [watchedBadge, setSelectedBadgeId]);

  const isNameExists = useMemo(
    (): boolean =>
      Boolean(
        getOffers.data?.offers?.find(
          (o: Offer) => o.name === values.name
        ) && !edit
      ),
    [getOffers.data?.offers, values.name, edit]
  );

  const isPublisherOfferIdTaken = useMemo(
    (): boolean =>
      !checkUniqueness(
        values?.publisherOfferId,
        'publisherOfferId',
        getOffers.data?.offers,
        data,
        edit && !dup
      ),
    [values?.publisherOfferId, getOffers.data?.offers, data, edit, dup]
  );

  const removeProduct = useCallback(
    (product: { _id: string; productId?: string }): void => {
      setDirtyProducts(true);
      setProducts((prev) => prev.filter((p) => p._id !== product._id));
    },
    []
  );

  const editProduct = useCallback(
    (product: IProductProps): void => {
      setDirtyProducts(true);
      removeProduct(product);
      setCurrentProduct({
        product: product.image,
        productId: product._id,
        quantity: product.amount || 0,
        publisherProductId: product.publisherProductId,
        priority: String(product.priority || EProductPriority.SUB)
      });
    },
    [removeProduct]
  );

  const handleProductReorder = useCallback(
    (reordered: IProductProps[]): void => {
      setDirtyProducts(true);
      setProducts(reordered);
    },
    [setDirtyProducts, setProducts]
  );

  const addProduct = useCallback(async (): Promise<void> => {
    const foundProduct = productsData!.result.find(
      (product: { productId: string | undefined }) =>
        product.productId === currentProduct?.productId
    );

    const formattedProductQuantity = await formatProductQuantity.mutateAsync({
      quantity: currentProduct!.quantity?.toString() || '',
      productType: foundProduct.type
    });

    const newProduct: IProductProps = {
      _id: currentProduct!.productId!,
      productId: currentProduct!.productId!,
      image: foundProduct!.images[0].url,
      imagePrefix:
        foundProduct.images?.find(
          (i: { type: EAssetType }) => i.type === EAssetType.PRODUCT_PREFIX
        )?.url || '',
      name: foundProduct!.name,
      amount: currentProduct!.quantity || 0,
      type: foundProduct!.type,
      textFontColorHex: foundProduct!.textFontColorHex,
      prefix: foundProduct!.prefix,
      suffix: foundProduct!.suffix,
      quantityDisplay: formattedProductQuantity.data,
      priority: currentProduct?.priority || EProductPriority.SUB,
      publisherProductId: currentProduct?.publisherProductId
    };

    setProducts((prev) => [...prev, newProduct]);
    setDirtyProducts(true);
    setCurrentProduct({
      productId: '',
      quantity: 0,
      publisherProductId: '',
      priority: EProductPriority.SUB
    });
  }, [productsData, currentProduct, formatProductQuantity]);

  const handleQuantityChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      let newQuantity: string | number = e.target.value;
      if (e.target.value.length !== 0) {
        newQuantity = transformQuantityValue(e.target.value);
      }
      setCurrentProduct({
        productId: currentProduct?.productId || '',
        quantity: newQuantity,
        publisherProductId: currentProduct?.publisherProductId,
        priority: currentProduct?.priority || EProductPriority.SUB
      });
    },
    [currentProduct]
  );

  const handlePriorityChange = useCallback(
    (value: string): void => {
      setCurrentProduct({
        productId: currentProduct?.productId || '',
        quantity: currentProduct?.quantity || 0,
        publisherProductId: currentProduct?.publisherProductId,
        priority: value
      });
    },
    [currentProduct]
  );

  const sectionItems = useMemo(() => {
    const items = [{ value: '0', label: 'None' }];
    if (getSections?.data?.result?.length > 0) {
      const parsedSectionsData = getSections.data.result.map(
        (section: Section) => ({
          value: section.sectionId,
          label: section.name
        })
      );
      return items.concat(parsedSectionsData);
    }
    return items;
  }, [getSections?.data?.result]);

  const tabItems = useMemo((): Array<{ value: string; label: string }> => {
    if (!enableTabs) return [];
    const base = [{ value: EMPTY_TAB.value, label: EMPTY_TAB.content }];
    if (getTabs.data?.length > 0) {
      const parsed = getTabs.data.map((tab: TabsItem) => ({
        value: tab.tabId,
        label: tab.name
      }));
      return base.concat(parsed);
    }
    return base;
  }, [enableTabs, getTabs.data]);

  const getProductsOptions = useMemo(() => {
    if (isLoadingProducts || !productsList) return [];
    const productIds = new Set(products.map((item) => item.productId));
    const filtered = productsList.filter(
      (product: Product) => !productIds.has(product.productId as string)
    );
    if (filtered.length === 0) return [];
    return filtered.map((product) => ({
      url: product.images?.[0]?.url || '',
      name: product.name,
      key: product.name,
      value: product.productId || ''
    }));
  }, [productsList, products, isLoadingProducts]);

  const selectedProduct = useMemo(
    () =>
      getProductsOptions.find(
        (product) => product.value === currentProduct?.productId
      ) || null,
    [getProductsOptions, currentProduct?.productId]
  );

  const priceLocalizedCheckDebounce = useCallback(
    (price: number): void => {
      if (priceTypingDebounceTimeout.current) {
        clearTimeout(priceTypingDebounceTimeout.current);
      }
      priceTypingDebounceTimeout.current = setTimeout(() => {
        setIsPriceLocalized(
          isPriceLocalizedUtil(price, getPricings.data?.result)
        );
      }, 1000);
    },
    [getPricings.data?.result]
  );

  useEffect(() => {
    if (getPricings.data && values.price) {
      priceLocalizedCheckDebounce(
        new Decimal(values.price).times(100).toNumber()
      );
    }
  }, [getPricings.data, values.price, priceLocalizedCheckDebounce]);

  const productsListForPreview = useMemo(
    () => buildProductsListForPreview(products),
    [products]
  );

  const formTitle = useMemo(
    (): string =>
      buildFormTitle(edit, 'Bundle Offer', getOffer.data?.result?.name),
    [edit, getOffer.data?.result?.name]
  );

  const isFormValid = useMemo(
    () =>
      !isLoading &&
      isValid &&
      isProgressBarFormValid &&
      canUserEdit &&
      !isPublisherOfferIdTaken &&
      (!edit || isDirty || dirtyProducts || dirtyProgressBar),
    [
      isLoading,
      isValid,
      isProgressBarFormValid,
      canUserEdit,
      isPublisherOfferIdTaken,
      edit,
      isDirty,
      dirtyProducts,
      dirtyProgressBar
    ]
  );

  const onSubmit = useCallback(
    (formValues: BundlesFormFields): void => {
      const offerBadges = buildOfferBadges(
        formValues.badge ?? null,
        formValues.publisherBadgeId ?? '',
        selectedPublisherBadgeId
      );

      const progressBarPoints = convertProgressBarPointsToApiFormat(
        progressBarFormValues
      );

      const { sectionId, tabId } = buildSectionTabPayload(
        formValues.section,
        formValues.tab
      );

      const newOffer: Partial<Offer> & {
        sectionId?: string | null;
        tabId?: string | null;
      } = {
        ...buildBaseOfferPayload(
          formValues,
          formValues.segments,
          OfferType.BUNDLE
        ),
        badges: offerBadges,
        productSale: {
          sale: formValues.salePercentage ?? 0,
          type:
            (formValues.salePercentageDisplayType as ESaleDiscountDisplayType) ||
            ESaleDiscountDisplayType.PERCENTAGE
        },
        priceDiscount: {
          discount: formValues.discount ?? 0,
          type:
            (formValues.priceDiscountDisplayType as ESaleDiscountDisplayType) ||
            ESaleDiscountDisplayType.PERCENTAGE
        },
        productsSequence: [
          {
            index: 1,
            products: products.map((p) => ({
              productId: p.productId,
              quantity: p.amount,
              ...(p.publisherProductId
                ? { publisherProductId: p.publisherProductId }
                : {}),
              priority: p.priority
            })),
            priceInUsdCents: new Decimal(formValues.price).mul(100).toNumber(),
            progressBarPoints
          }
        ],
        sectionId,
        tabId
      };

      submitOffer(newOffer, offerId);
    },
    [
      products,
      progressBarFormValues,
      selectedPublisherBadgeId,
      offerId,
      submitOffer
    ]
  );

  const handleSave = useCallback((): void => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const handleClose = useCallback((): void => {
    navigate('../');
  }, [navigate]);

  const handleSegmentsChange = useCallback(
    (newSegments: string[]): void => {
      setChosenSegment(newSegments);
      setValue('segments', newSegments, { shouldDirty: true });
    },
    [setChosenSegment, setValue]
  );

  const handleTagsChange = useCallback(
    (newTags: string[]): void => {
      setValue('tags', newTags, { shouldDirty: true });
    },
    [setValue]
  );

  const badgeListOptions = useMemo(
    () =>
      badgeList?.map((item: { value: string; content: string }) => ({
        value: item.value,
        label: item.content
      })) || [],
    [badgeList]
  );

  const mappedCurrentProduct = useMemo(() => {
    if (!currentProduct) return null;
    return {
      productId: currentProduct.productId || '',
      quantity: currentProduct.quantity || 0,
      priority: currentProduct.priority?.toString() || EProductPriority.SUB,
      publisherProductId: currentProduct.publisherProductId
    };
  }, [currentProduct]);

  const handleCurrentProductChange = useCallback(
    (product: { productId: string; quantity: number | string; priority: string; publisherProductId?: string } | null): void => {
      if (product) {
        setCurrentProduct({
          productId: product.productId,
          quantity: product.quantity,
          publisherProductId: product.publisherProductId,
          priority: product.priority
        });
      } else {
        setCurrentProduct(null);
      }
    },
    []
  );

  const isAddDisabled = !currentProduct?.productId || !currentProduct?.quantity;

  const progressBarInitialValues = useMemo(() => ({
    progressBarId: progressBarFormValues.progressBarId,
    progressBarPoints: progressBarFormValues.progressBarPoints
  }), [progressBarFormValues.progressBarId, progressBarFormValues.progressBarPoints]);

  return (
    <OfferFormLayout
      title={formTitle}
      isFormValid={isFormValid}
      onSave={handleSave}
      onClose={handleClose}
      previewContent={
        <BundlePreview
          getOfferUI={getOfferUI}
          productsListForPreview={productsListForPreview}
          price={values.price}
          salePercentage={values.salePercentage}
          salePercentageDisplayType={
            values.salePercentageDisplayType as ESaleDiscountDisplayType
          }
          emblemBadges={emblemBadges}
          ribbonBadges={ribbonBadges}
          progressBarFormValues={progressBarFormValues}
          progressBarOffers={progressBarOffers}
        />
      }
    >
      <Form {...form}>
        <div className="flex flex-col h-full pt-10 pb-10 w-[528px] min-h-fit">
          <GeneralSection
            offerDesignOptions={offerDesignOptions}
            onOfferDesignChange={setCurrentOfferUI}
            isNameTaken={isNameExists}
            isPublisherOfferIdTaken={isPublisherOfferIdTaken}
            isPriceLocalized={isPriceLocalized}
            sectionItems={sectionItems}
            tabItems={tabItems}
            showSection
            showTabs={!!enableTabs}
            sectionDataTestId="bundleSectionInput"
            tabDataTestId="bundleTabInput"
            nameDescriptionDataTestId="bundleNameValidationTxt"
            externalIdDescriptionDataTestId="bundleSkuValidationTxt"
            offerDesignDescriptionDataTestId="bundleOfferDesignValidationTxt"
            priceDescriptionDataTestId="bundlePriceValidationTxt"
          />
          <Separator className="my-9 bg-base-border" />
          <SegmentsSection
            items={chosenSegment}
            onItemsChange={handleSegmentsChange}
            inputTestId="promoSegmentsInpt"
          />
          <Separator className="my-9 bg-base-border" />
          <BadgesSection
            relevantBadgeType={relevantBadgeType}
            badgeList={badgeListOptions}
          />
          <Separator className="my-9 bg-base-border" />
          <SaleDiscountSection
            showPriceDiscount={values.price > 0}
            saleLabel="First Product Sale"
            saleTypeDataTestId="productSaleTypeDropDown"
            priceDiscountTypeDataTestId="priceDiscountTypeDropDown"
            discountInputDataTestId="bundlePriceDiscountInpt"
            saleDescriptionDataTestId="bundleProductSaleValidationTxt"
            priceDiscountDescriptionDataTestId="bundlePriceDiscountValidationTxt"
          />
          <Separator className="my-9 bg-base-border" />
          <OfferProgressBarSection
            initialValues={progressBarInitialValues}
            onChange={handleProgressBarFormChange}
            progressBarOffers={progressBarOffers}
          />
          <Separator className="my-9 bg-base-border" />
          <ProductsSection
            productOptions={getProductsOptions}
            currentProduct={mappedCurrentProduct}
            onCurrentProductChange={handleCurrentProductChange}
            onQuantityChange={handleQuantityChange}
            onPriorityChange={handlePriorityChange}
            onAddProduct={addProduct}
            isAddDisabled={isAddDisabled}
            products={products}
            onEditProduct={editProduct}
            onDeleteProduct={removeProduct}
            onReorderProducts={handleProductReorder}
            selectedProduct={selectedProduct}
            addButtonDataTestId="bundleAddProductBtn"
            productListDataTestId="dashBundleProductList"
            priorityListDataTestId="bundleMainSubInpt"
          />
          {!!hasFeatureFlagTags && (
            <React.Fragment>
              <Separator className="my-9 bg-base-border" />
              <TagsSection
                items={values.tags || []}
                onItemsChange={handleTagsChange}
                inputTestId="bundleTagsInput"
              />
            </React.Fragment>
          )}
        </div>
      </Form>
    </OfferFormLayout>
  );
};
